import React, { useState } from "react";
import MembershipTab from "./MembershipTab";
import AttendanceTab from "./AttendanceTab";
import FollowupTeamTab from "./FollowupTeamTab";
import AuditLogTab from "./AuditLogTab";

// ============================================================
// AnalyticsPage — accessible to Super Admin, Limited Admin, Pastor.
// Audit Log tab is gated to Super Admin + Limited Admin only.
// ============================================================
export default function AnalyticsPage({
  currentUser, isSuperAdmin, isLimitedAdmin,
  members, families, services, events, attendance, followups, users, audit, navigate,
}) {
  const canSeeAudit = !!(isSuperAdmin || isLimitedAdmin);
  const [tab, setTab] = useState("membership");

  const TABS = [
    { id: "membership",  label: "Membership" },
    { id: "attendance",  label: "Attendance" },
    { id: "followup",    label: "Follow-Up Team" },
    ...(canSeeAudit ? [{ id: "audit", label: "Audit Log" }] : []),
  ];

  return (
    <div data-testid="analytics-page">
      <style>{ANALYTICS_CSS}</style>

      <header className="analytics-header">
        <h1 className="analytics-title">ANALYTICS</h1>
        <p className="analytics-subtitle">Church-wide insights, broken down by ministry and team.</p>
      </header>

      <nav className="analytics-tabs" role="tablist" aria-label="Analytics categories">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            data-testid={`analytics-tab-${t.id}`}
            className={`analytics-tab ${tab === t.id ? "active" : ""}`}
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="analytics-panel">
        {tab === "membership" && (
          <MembershipTab members={members || []} families={families || []} navigate={navigate} />
        )}
        {tab === "attendance" && (
          <AttendanceTab services={services || []} events={events || []} attendance={attendance || []} members={members || []} />
        )}
        {tab === "followup" && (
          <FollowupTeamTab users={users || []} followups={followups || []} />
        )}
        {tab === "audit" && canSeeAudit && (
          <AuditLogTab audit={audit || []} />
        )}
      </div>
    </div>
  );
}

const ANALYTICS_CSS = `
  /* ===== Analytics page ===== */
  .analytics-header { margin-bottom: 18px; }
  .analytics-title {
    font-family: 'Cinzel', serif; font-size: 28px; font-weight: 700;
    color: var(--kc-text); letter-spacing: 0.08em; text-transform: uppercase; margin: 0;
  }
  @media (min-width: 720px) { .analytics-title { font-size: 34px; } }
  .analytics-subtitle {
    font-family: 'Montserrat', sans-serif; font-size: 13px;
    color: var(--kc-sub); margin: 6px 0 0;
  }

  .analytics-tabs {
    display: flex; gap: 6px; flex-wrap: wrap;
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    margin-bottom: 22px; padding-bottom: 1px;
  }
  .analytics-tab {
    background: transparent; border: none; padding: 11px 16px;
    color: var(--kc-sub);
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
    border-bottom: 2px solid transparent; transition: color 0.15s, border-color 0.15s;
    margin-bottom: -1px;
  }
  .analytics-tab:hover { color: var(--kc-accent); }
  .analytics-tab.active { color: var(--kc-accent); border-bottom-color: var(--kc-accent); }

  .analytics-section { margin-bottom: 32px; }
  .analytics-section-title {
    font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700;
    color: var(--kc-accent); letter-spacing: 0.10em; text-transform: uppercase;
    margin: 0 0 12px;
  }

  .analytics-chip-grid {
    display: grid; gap: 10px;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    margin-bottom: 14px;
  }
  .analytics-chip {
    background: var(--kc-elevated); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px; padding: 12px 14px; cursor: pointer;
    color: var(--kc-text); text-align: left; transition: border-color 0.15s, background 0.15s, transform 0.12s;
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    font-family: 'Montserrat', sans-serif;
  }
  .analytics-chip:hover { border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); background: color-mix(in srgb, var(--kc-accent) 6%, transparent); transform: translateY(-1px); }
  .analytics-chip .ac-label { font-size: 12px; font-weight: 600; }
  .analytics-chip .ac-count {
    font-family: 'Cinzel', serif; font-size: 18px; font-weight: 700; color: var(--kc-accent);
  }

  .analytics-empty {
    background: rgba(255,255,255,0.03); border: 1px dashed rgba(255,255,255,0.10);
    border-radius: 10px; padding: 18px; text-align: center;
    color: var(--kc-sub); font-family: 'Montserrat', sans-serif; font-size: 13px;
  }

  /* Age groups */
  .age-groups {
    display: grid; gap: 14px; margin-top: 18px;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  .age-group-card {
    background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 12px; padding: 16px;
  }
  .age-group-header { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
  .age-group-title { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.05em; text-transform: uppercase; }
  .age-group-meta  { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-sub); }
  .age-group-subs  { display: flex; flex-direction: column; gap: 6px; }
  .age-sub-chip {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);
    border-radius: 9px; padding: 9px 11px; cursor: pointer; color: var(--kc-text);
    display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center;
    font-family: 'Montserrat', sans-serif; transition: border-color 0.15s, background 0.15s;
  }
  .age-sub-chip:hover { border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); background: color-mix(in srgb, var(--kc-accent) 6%, transparent); }
  .age-sub-chip .asc-label { font-size: 12px; font-weight: 600; }
  .age-sub-chip .asc-range { font-size: 10px; color: var(--kc-meta); letter-spacing: 0.04em; }
  .age-sub-chip .asc-count { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--kc-accent); min-width: 24px; text-align: right; }

  /* Inline cards (new members + baptism) */
  .analytics-inline-card {
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent);
    border-radius: 12px; padding: 14px;
  }
  .analytics-inline-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
  .analytics-inline-count { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-sub); }
  .analytics-period-select {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text);
    border-radius: 8px; padding: 7px 10px; font-family: 'Montserrat', sans-serif; font-size: 12px;
  }

  .analytics-row-list { display: flex; flex-direction: column; gap: 6px; max-height: 360px; overflow-y: auto; }
  .analytics-row {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.05);
    border-radius: 10px; padding: 9px 12px; display: flex; align-items: center; gap: 12px;
    text-align: left; cursor: pointer; color: var(--kc-text); font-family: 'Montserrat', sans-serif;
    transition: border-color 0.12s, background 0.12s;
  }
  .analytics-row:hover { border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); background: color-mix(in srgb, var(--kc-accent) 4%, transparent); }
  .analytics-row .ar-main { flex: 1; min-width: 0; }
  .analytics-row .ar-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .analytics-row .ar-meta { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }
  .analytics-row .ar-arrow { color: var(--kc-meta); font-size: 18px; }

  /* Family Dynamic 4-grid */
  .family-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
  .family-card { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 12px; padding: 14px; }
  .family-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .family-card-icon { font-size: 18px; }
  .family-card-title { flex: 1; font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.06em; text-transform: uppercase; }
  .family-card-count { font-family: 'Cinzel', serif; font-size: 18px; font-weight: 700; color: var(--kc-accent); }
  .family-card-list { display: flex; flex-direction: column; gap: 4px; }
  .family-row { padding: 8px 10px; background: rgba(255,255,255,0.03); border-radius: 8px; }
  .family-row .fr-name { font-size: 12px; font-weight: 600; color: var(--kc-text); }
  .family-row .fr-meta { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }
  .family-more { font-size: 11px; color: var(--kc-meta); padding: 6px 10px; font-style: italic; }

  /* Attendance tab */
  .att-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 16px; }
  .att-toolbar-counts { font-size: 12px; color: var(--kc-sub); font-family: 'Montserrat', sans-serif; }
  .att-toolbar-filters { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  .att-filter { display: flex; align-items: center; gap: 6px; font-family: 'Montserrat', sans-serif; }
  .att-filter span { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--kc-sub); }
  .att-filter input {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text);
    border-radius: 8px; padding: 7px 9px; font-size: 12px;
  }
  .att-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
  .att-card { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 12px; padding: 16px; }
  .att-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
  .att-card-title { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .att-card-kind { font-size: 10px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-meta); }
  .att-card-name { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.04em; text-transform: uppercase; }
  .att-card-rate { font-family: 'Cinzel', serif; font-size: 26px; font-weight: 700; color: var(--kc-accent); line-height: 1; }
  .att-card-bar { height: 8px; background: rgba(255,255,255,0.06); border-radius: 4px; overflow: hidden; margin-bottom: 10px; }
  .att-card-bar-fill { height: 100%; background: var(--kc-accent-grad); border-radius: 4px; transition: width 0.35s; }
  .att-card-meta { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
  .att-meta-link {
    background: transparent; border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text);
    padding: 5px 9px; border-radius: 7px; cursor: pointer; font-size: 11px; font-family: 'Montserrat', sans-serif; font-weight: 600;
    transition: border-color 0.12s, background 0.12s;
  }
  .att-meta-link:disabled { opacity: 0.4; cursor: default; }
  .att-meta-link:not(:disabled).present:hover { border-color: #4ade80; background: rgba(74,222,128,0.10); }
  .att-meta-link:not(:disabled).absent:hover  { border-color: #ff6b6b; background: rgba(255,107,107,0.10); }
  .att-meta-sessions { font-size: 11px; color: var(--kc-meta); margin-left: auto; }
  .att-name-list { padding-left: 22px; margin: 0; max-height: 60vh; overflow-y: auto; }
  .att-name-row { padding: 6px 0; font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text); border-bottom: 1px solid rgba(255,255,255,0.05); }

  /* Follow-Up Team tab */
  .fut-stats-bar { display: flex; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
  .fut-stat { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 10px; padding: 10px 16px; display: flex; flex-direction: column; gap: 4px; min-width: 110px; }
  .fut-stat-num { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; color: var(--kc-accent); line-height: 1; }
  .fut-stat-label { font-size: 10px; color: var(--kc-sub); text-transform: uppercase; letter-spacing: 0.06em; }
  .fut-stat-warn .fut-stat-num { color: #ff6b6b; }
  .fut-list { display: flex; flex-direction: column; gap: 8px; }
  .fut-row {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.05);
    border-radius: 12px; padding: 12px 14px; display: grid;
    grid-template-columns: auto 1fr auto auto; gap: 14px; align-items: center;
  }
  @media (max-width: 720px) {
    .fut-row { grid-template-columns: auto 1fr auto; }
    .fut-row .fut-row-stats { grid-column: 1 / -1; justify-content: flex-start; }
  }
  .fut-row-main { min-width: 0; }
  .fut-row-name { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: var(--kc-text); }
  .fut-row-role { font-size: 10px; color: var(--kc-meta); text-transform: uppercase; letter-spacing: 0.06em; margin-top: 3px; }
  .fut-row-stats { display: flex; gap: 14px; }
  .fut-stat-cell { display: flex; flex-direction: column; gap: 2px; min-width: 64px; text-align: center; }
  .fut-stat-cell-value { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 700; color: var(--kc-text); line-height: 1; }
  .fut-stat-cell-value.small { font-size: 11px; font-family: 'Montserrat', sans-serif; font-weight: 600; color: var(--kc-text); }
  .fut-stat-cell-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-meta); }
  .fut-badge { padding: 5px 9px; border-radius: 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; font-family: 'Montserrat', sans-serif; }
  .fut-badge.is-active { background: rgba(74,222,128,0.12); color: #4ade80; border: 1px solid rgba(74,222,128,0.35); }
  .fut-badge.is-inactive { background: rgba(255,107,107,0.12); color: #ff6b6b; border: 1px solid rgba(255,107,107,0.35); }

  /* Audit Log tab */
  .audit-toolbar { display: flex; gap: 14px; justify-content: space-between; align-items: center; margin-bottom: 14px; flex-wrap: wrap; }
  .audit-search { background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text); border-radius: 8px; padding: 8px 12px; font-family: 'Montserrat', sans-serif; font-size: 13px; min-width: 220px; flex: 1; max-width: 360px; }
  .audit-table-wrap { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 12px; overflow: hidden; }
  .audit-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .audit-table { width: 100%; min-width: 640px; border-collapse: collapse; font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text); }
  .audit-table thead th { background: var(--kc-elevated); padding: 10px 12px; text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--kc-sub); border-bottom: 1px solid rgba(255,255,255,0.05); white-space: nowrap; }
  .audit-table tbody td { padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.05); }
  .audit-table tbody tr:last-child td { border-bottom: none; }
  .audit-ts { font-size: 11px; color: var(--kc-sub); white-space: nowrap; }
  .audit-action { font-weight: 600; }
  .audit-detail { color: var(--kc-sub); }

  /* Shared filtered-members modal */
  .fm-modal { width: min(640px, 100%); max-height: 80vh; display: flex; flex-direction: column; }
  .fm-modal-body { overflow-y: auto; }
  .fm-modal-sub { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }
  .fm-modal-count { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-accent); margin-top: 2px; letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }
  .fm-list { display: flex; flex-direction: column; gap: 6px; }
  .fm-row {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.05); border-radius: 10px;
    padding: 10px 12px; display: flex; align-items: center; gap: 12px; text-align: left; cursor: pointer; color: var(--kc-text);
    font-family: 'Montserrat', sans-serif; transition: border-color 0.12s, background 0.12s;
  }
  .fm-row:hover { border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); background: color-mix(in srgb, var(--kc-accent) 4%, transparent); }
  .fm-row .fm-row-main { flex: 1; min-width: 0; }
  .fm-row .fm-row-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fm-row .fm-row-meta { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }
  .fm-row .fm-row-arrow { color: var(--kc-meta); font-size: 18px; }
`;
